import React, { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { PlusOutlined } from '@ant-design/icons';
import { Button, message, Row, Typography } from 'antd';

import { DEFAULT_PAGE_SIZE } from 'appConstants';
import { DragableRow } from 'components';
import { SearchBar } from 'components/SearchBar';
import { TableWithNoWrapHeader } from 'components/StyledTable';

import { changeCardItemOrderApi, changeCardOrderApi } from './api';

const BDOSettingsCardComponents = ({
  cardTitle,
  data,
  params,
  columns,
  onAddNewClick,
  paramsDispatch,
  mutate,
  loading = false,
  isCreditCard = false
}) => {
  const [dndLoading, setDndLoading] = useState(false);

  const moveRow = async (dragIndex: number, hoverIndex: number) => {
    if (loading || dndLoading) return;
    if (dragIndex === hoverIndex) return;

    const rows = data?.data ?? [];
    if (rows.length === 0) return;

    const updatedData = [...rows];
    const [draggedRow] = updatedData.splice(dragIndex, 1);
    updatedData.splice(hoverIndex, 0, draggedRow);

    // sort_order is global across all pages; offset by the current page to
    // avoid overwriting other pages' ordering when reordering on page > 1.
    const pageOffset = ((params?.page ?? 1) - 1) * (params?.size ?? DEFAULT_PAGE_SIZE);
    const updatedRows = updatedData.map((row, index) => ({
      id: row.id,
      sort_order: pageOffset + index + 1
    }));

    try {
      setDndLoading(true);
      if (isCreditCard) await changeCardOrderApi(updatedRows);
      else await changeCardItemOrderApi(updatedRows);
      mutate();
    } catch (error) {
      message.error('Error sorting the rows');
    } finally {
      setDndLoading(false);
    }
  };

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Typography.Title level={4}>{cardTitle}</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={onAddNewClick}>
          Add New
        </Button>
      </Row>
      <SearchBar
        onSearch={value => paramsDispatch({ type: 'SEARCH', value })}
        placeholder="Search"
      />
      <DndProvider backend={HTML5Backend}>
        <TableWithNoWrapHeader<any>
          style={{ marginTop: 16 }}
          columns={columns}
          onChange={pagination =>
            paramsDispatch({ type: 'PAGINATION', current: pagination.current })
          }
          dataSource={data?.data || []}
          rowKey={r => r.id}
          onRow={(_, index) => ({
            index,
            moveRow
          })}
          components={{
            body: {
              row: DragableRow
            }
          }}
          pagination={{
            showSizeChanger: true,
            onShowSizeChange: (current, size) =>
              paramsDispatch({ type: 'PAGINATION', current, size }),
            showQuickJumper: true,
            pageSizeOptions: ['5', '10', '20', '50', '75', '100', '200'],
            position: ['bottomRight'],
            current: params.page,
            defaultPageSize: DEFAULT_PAGE_SIZE,
            pageSize: params.size,
            total: data?.meta?.total_count || 0
          }}
          loading={loading || dndLoading}
        />
      </DndProvider>
    </>
  );
};

export default BDOSettingsCardComponents;
